import React from "react";
import { SITE } from "@/lib/site";

const SITE_URL = "https://hgcorporates.com";
const DEFAULT_IMAGE = `${SITE_URL}/og.jpg`;

/**
 * Global site-level JSON-LD (Organization + WebSite).
 * Emitted on every page so AI/search crawlers can resolve brand context
 * regardless of which URL they land on.
 */
const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "HG Corporates",
  alternateName: SITE.parent,
  url: SITE_URL,
  logo: `${SITE_URL}/brand/logo.png`,
  image: DEFAULT_IMAGE,
  email: SITE.email,
  telephone: "+91-9797193555",
  founder: { "@type": "Person", name: SITE.founder },
  sameAs: [SITE.googleReviewsUrl],
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.708, 7th Floor, Nirmal Ecstasy Business Park, Near East-West Flyover",
    addressLocality: "Mulund (West), Mumbai",
    addressRegion: "Maharashtra",
    postalCode: "400080",
    addressCountry: "IN",
  },
  areaServed: [
    { "@type": "City", name: "Mumbai" },
    { "@type": "City", name: "Navi Mumbai" },
    { "@type": "City", name: "Thane" },
    { "@type": "AdministrativeArea", name: "Mumbai Metropolitan Region (MMRDA)" },
    { "@type": "Country", name: "India" },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: String(SITE.googleRating),
    reviewCount: String(SITE.googleReviewCount),
    bestRating: "5",
    worstRating: "1",
  },
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "HG Corporates",
  alternateName: ["HG Corporates (hgcorporates.com)", "HGCorporates"],
  url: SITE_URL,
  publisher: { "@type": "Organization", name: "HG Corporates" },
  inLanguage: "en-IN",
};

/**
 * <Seo> — single source of truth for per-route metadata.
 *
 * Props:
 *  - title:        <title> and og:title / twitter:title
 *  - description:  <meta description> + og:description + twitter:description
 *  - path:         route path (e.g. "/about"); used to build canonical + og:url
 *  - image:        og:image / twitter:image (absolute URL preferred)
 *  - type:         og:type (default "website"; use "article" for blog posts)
 *  - noindex:      set robots to noindex when true
 *  - jsonLd:       single object OR array of objects to emit as JSON-LD
 *  - breadcrumbs:  [{ name, path }] used to auto-emit BreadcrumbList JSON-LD
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
  breadcrumbs,
}) {
  const canonical = `${SITE_URL}${path === "/" ? "/" : path.replace(/\/$/, "")}`;
  const ogImage = image?.startsWith("http") ? image : `${SITE_URL}${image}`;

  const webPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url: canonical,
    inLanguage: "en-IN",
    isPartOf: { "@type": "WebSite", name: "HG Corporates", url: SITE_URL },
    publisher: { "@type": "Organization", name: "HG Corporates" },
  };

  const breadcrumbJsonLd = breadcrumbs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${SITE_URL}${b.path}`,
        })),
      }
    : null;

  const extraJsonLd = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="HG Corporates" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }} />
      {breadcrumbJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      )}
      {extraJsonLd.map((data, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
      ))}
    </>
  );
}
